import { apiClient } from './api'

export interface Organization {
  organization_id: string
  name: string
  description?: string
  is_active: boolean
  created_at: string
  updated_at: string
  member_count?: number
}

export interface OrganizationCreate {
  name: string
  description?: string
}

export interface OrganizationUpdate {
  name?: string
  description?: string
}

export interface Member {
  member_id: string
  organization_id: string
  user_id: string
  email: string
  full_name: string
  role: 'owner' | 'admin' | 'member'
  invited_by?: string
  joined_at: string
  is_active: boolean
}

export interface MemberInvite {
  email: string
  role: 'owner' | 'admin' | 'member'
}

export interface MemberUpdate {
  role: 'owner' | 'admin' | 'member'
}

export interface MemberListResponse {
  members: Member[]
  total: number
}

export const organizationService = {
  // Get organization details
  getOrganization: async (organizationId: string): Promise<Organization> => {
    const response = await apiClient.get<Organization>(
      `/organizations/${organizationId}`
    )
    return response.data
  },

  // Create organization
  createOrganization: async (
    data: OrganizationCreate
  ): Promise<Organization> => {
    const response = await apiClient.post<Organization>('/organizations', data)
    return response.data
  },

  // Update organization
  updateOrganization: async (
    organizationId: string,
    data: OrganizationUpdate
  ): Promise<Organization> => {
    const response = await apiClient.put<Organization>(
      `/organizations/${organizationId}`,
      data
    )
    return response.data
  },

  // Delete organization
  deleteOrganization: async (organizationId: string): Promise<void> => {
    await apiClient.delete(`/organizations/${organizationId}`)
  },

  // List members
  listMembers: async (
    organizationId: string
  ): Promise<MemberListResponse> => {
    const response = await apiClient.get<MemberListResponse>(
      `/organizations/${organizationId}/members`
    )
    return response.data
  },

  // Invite member
  inviteMember: async (
    organizationId: string,
    data: MemberInvite
  ): Promise<Member> => {
    const response = await apiClient.post<Member>(
      `/organizations/${organizationId}/members`,
      data
    )
    return response.data
  },

  // Update member role
  updateMemberRole: async (
    organizationId: string,
    memberId: string,
    data: MemberUpdate
  ): Promise<Member> => {
    const response = await apiClient.put<Member>(
      `/organizations/${organizationId}/members/${memberId}`,
      data
    )
    return response.data
  },

  // Remove member
  removeMember: async (
    organizationId: string,
    memberId: string
  ): Promise<void> => {
    await apiClient.delete(
      `/organizations/${organizationId}/members/${memberId}`
    )
  },
}

